import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../i18n';
import type { Contract, ContractStatus, ContractType } from './api';
import {
  useCheckContractExpiry,
  useCreateContract,
  useDeleteContract,
  useUpdateContract,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Vorlauf des Ablaufhinweises in Tagen.
 *
 * Deckungsgleich mit der Vorgabe des Endpunkts. Ein eigenes Eingabefeld
 * dafuer waere eine Einstellung, die einmal gesetzt und nie wieder angesehen
 * wird -- und die zweite Wahrheit neben der des Servers.
 */
const EXPIRY_LEAD_DAYS = 30;

interface ContractPanelProps {
  customerId: string;
  contracts: Contract[];
  canWrite: boolean;
}

const TYPES: readonly ContractType[] = [
  'managed_service',
  'retainer',
  'time_and_material',
  'project',
  'warranty',
];

const STATUSES: readonly ContractStatus[] = ['draft', 'active', 'expired', 'terminated'];

/**
 * Die Servicevereinbarungen eines Kunden -- F2-05, K-180.
 *
 * **Ohne Abrechnung.** "Vereinbarte Stunden" ist ein Nachweis, kein Preis: Er
 * zeigt, wie viel im Monat zugesagt ist, damit eine Auswertung zeigen kann,
 * wie viel verbraucht wurde. Was eine Stunde kostet, steht hier nicht --
 * docs/01 fuehrt Rechnungsstellung als Nicht-Ziel.
 *
 * Ein Vertrag, dessen Ende naeher als dreissig Tage liegt, wird hervorgehoben.
 * Die Warnung per Mail kommt mit Phase 3; bis dahin ist das Auge die
 * Erinnerung.
 */
export function ContractPanel({ customerId, contracts, canWrite }: ContractPanelProps) {
  const { t, i18n } = useTranslation();
  const create = useCreateContract(customerId);
  const remove = useDeleteContract();
  const update = useUpdateContract();
  const checkExpiry = useCheckContractExpiry();

  // **Bearbeiten statt loeschen und neu anlegen.** Ein Vertrag traegt die
  // Servicezusage, nach der die Fristen an jedem Ticket dieses Kunden
  // gerechnet werden; ihn wegen eines verlaengerten Enddatums wegzuwerfen
  // hiesse, die Zusage neu zu erfinden.
  const [editing, setEditing] = useState<string | null>(null);

  const [values, setValues] = useState({
    name: '',
    contract_number: '',
    contract_type: 'managed_service' as ContractType,
    status: 'draft' as ContractStatus,
    valid_from: '',
    valid_until: '',
    auto_renew: false,
    included_hours: '',
  });

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const reset = () => {
    setEditing(null);
    setValues({
      name: '',
      contract_number: '',
      contract_type: 'managed_service',
      status: 'draft',
      valid_from: '',
      valid_until: '',
      auto_renew: false,
      included_hours: '',
    });
  };

  return (
    <div className="stack">
      {/* Der Lauf haengt am Taktgeber und lief bisher nur dort -- ohne diesen
          Knopf liesse sich nicht nachsehen, ob er ueberhaupt etwas findet.
          Dieselbe Ueberlegung wie beim Postfachabruf: Nach dem Einrichten
          will man es sofort wissen (F2-06, K-186). */}
      {canWrite ? (
        <div className="row">
          <Button
            variant="secondary"
            busy={checkExpiry.isPending}
            onClick={() => {
              checkExpiry.mutate(EXPIRY_LEAD_DAYS);
            }}
          >
            {t('crm.checkExpiry')}
          </Button>
          <span className="field__hint">
            {checkExpiry.data === undefined
              ? t('crm.checkExpiryHint', { count: EXPIRY_LEAD_DAYS })
              : t('crm.checkExpiryResult', { count: checkExpiry.data.length })}
          </span>
        </div>
      ) : null}

      {checkExpiry.isError ? <ErrorNotice error={checkExpiry.error} /> : null}

      <section className="card card--flush">
        {contracts.length === 0 ? (
          <EmptyState icon="crm" title={t('crm.noContracts')} hint={t('crm.noContractsHint')} />
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('crm.contractNumber')}</th>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('crm.contractType')}</th>
                  <th scope="col">{t('crm.validUntil')}</th>
                  <th scope="col">{t('crm.status')}</th>
                  {canWrite ? <th scope="col">{t('tickets.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td className="table__number">{contract.contract_number}</td>
                    <td>{contract.name}</td>
                    <td className="table__muted">{t(`contractType.${contract.contract_type}`)}</td>
                    <td>
                      {formatDate(contract.valid_until, i18n.language)}
                      {isExpiringSoon(contract) ? (
                        <>
                          {' '}
                          <Badge tone="warning" dot>
                            {t('crm.expiringSoon')}
                          </Badge>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Badge tone={contract.status === 'active' ? 'success' : 'neutral'}>
                        {t(`contractStatus.${contract.status}`)}
                      </Badge>
                    </td>
                    {canWrite ? (
                      <td>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditing(contract.id);
                            setValues({
                              name: contract.name,
                              contract_number: contract.contract_number,
                              contract_type: contract.contract_type,
                              status: contract.status,
                              valid_from: contract.valid_from,
                              valid_until: contract.valid_until,
                              auto_renew: contract.auto_renew,
                              included_hours: contract.included_hours ?? '',
                            });
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(contract.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {canWrite ? (
        <section className="card">
          <h2>{editing === null ? t('crm.newContract') : t('crm.editContract')}</h2>

          <div className="field">
            <label className="field__label" htmlFor="contract-name">
              {t('masterdata.name')}
            </label>
            <input
              id="contract-name"
              className="field__control"
              value={values.name}
              onChange={(event) => {
                set('name', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contract-type">
              {t('crm.contractType')}
            </label>
            <Select
              id="contract-type"
              className="field__control"
              value={values.contract_type}
              onChange={(event) => {
                set('contract_type', event.target.value as ContractType);
              }}
            >
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`contractType.${value}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contract-status">
              {t('crm.status')}
            </label>
            <Select
              id="contract-status"
              className="field__control"
              value={values.status}
              onChange={(event) => {
                set('status', event.target.value as ContractStatus);
              }}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`contractStatus.${value}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="row">
            <div className="field">
              <label className="field__label" htmlFor="contract-from">
                {t('crm.validFrom')}
              </label>
              <input
                id="contract-from"
                type="date"
                className="field__control"
                value={values.valid_from}
                onChange={(event) => {
                  set('valid_from', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="contract-until">
                {t('crm.validUntil')}
              </label>
              <input
                id="contract-until"
                type="date"
                className="field__control"
                value={values.valid_until}
                onChange={(event) => {
                  set('valid_until', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="contract-hours">
                {t('crm.includedHours')}
              </label>
              <input
                id="contract-hours"
                type="number"
                min="0"
                step="0.25"
                className="field__control"
                value={values.included_hours}
                onChange={(event) => {
                  set('included_hours', event.target.value);
                }}
              />
              <span className="field__hint">{t('crm.includedHoursHint')}</span>
            </div>
          </div>

          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={values.auto_renew}
              onChange={(event) => {
                set('auto_renew', event.target.checked);
              }}
            />
            {t('crm.autoRenew')}
          </label>

          {create.isError ? <ErrorNotice error={create.error} /> : null}
          {update.isError ? <ErrorNotice error={update.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={!values.name.trim() || !values.valid_from || !values.valid_until}
              busy={create.isPending || update.isPending}
              onClick={() => {
                const payload = {
                  name: values.name.trim(),
                  contract_type: values.contract_type,
                  status: values.status,
                  valid_from: values.valid_from,
                  valid_until: values.valid_until,
                  auto_renew: values.auto_renew,
                  ...(values.contract_number
                    ? { contract_number: values.contract_number.trim() }
                    : {}),
                  ...(values.included_hours ? { included_hours: values.included_hours } : {}),
                };

                if (editing !== null) {
                  update.mutate({ contractId: editing, payload }, { onSuccess: reset });
                  return;
                }
                create.mutate(payload, {
                  onSuccess: reset,
                });
              }}
            >
              {editing === null ? t('masterdata.add') : t('app.save')}
            </Button>
            {editing !== null ? (
              <Button variant="ghost" onClick={reset}>
                {t('app.cancel')}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** Endet der Vertrag innerhalb der naechsten dreissig Tage? */
function isExpiringSoon(contract: Contract): boolean {
  if (contract.status !== 'active') return false;
  const ends = new Date(contract.valid_until).getTime();
  const inThirtyDays = Date.now() + 30 * 24 * 60 * 60 * 1000;
  return ends <= inThirtyDays;
}
